import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    Divider,
    CircularProgress,
    Chip,
    Tabs,
    Tab,
} from '@mui/material';
import {
    Save as SaveIcon,
    Info as InfoIcon,
    RestartAlt as RestartAltIcon,
    Email as EmailIcon,
    Message as MessageIcon,
    Notifications as NotificationsIcon,
    ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import styled from '@emotion/styled';
import { AdminPageContainer, StaticCard } from '../../../components/admin/sharedStyles';
import PageHeader from '../../../components/PageHeader';
import ConfigSubMenu from '../../../components/admin/ConfigSubMenu';
import MarkdownEditor from '../../../components/MarkdownEditor';
import HoverTooltip from '../../../components/HoverTooltip';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../../redux/slices/alerts';
import { getConfigValue, saveConfig } from '../../../redux/slices/config';

const CHANNEL_KEYS = { email: 'notificationTemplatesEmail', inApp: 'notificationTemplatesInApp', apprise: 'notificationTemplatesApprise' };
const CHANNEL_LABELS = { email: 'Email', inApp: 'In-App', apprise: 'Apprise' };
const CHANNEL_ICONS = { email: <EmailIcon />, inApp: <NotificationsIcon />, apprise: <MessageIcon /> };

const hasValue = (v) => (typeof v === 'string' ? v.trim() !== '' : v !== undefined && v !== null);

const GlobalChannelSelector = styled(Box)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
}));

const EventHeader = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
    gap: 8,
});

const EventNameRow = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const LoadingContainer = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 32,
    paddingBottom: 32,
});

const StickyFooter = styled(Box)(({ theme }) => ({
    position: 'sticky',
    bottom: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(2),
    background: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
    zIndex: 10,
}));

const NavPanel = styled(Box)(({ theme }) => ({
    borderRight: `1px solid ${theme.palette.divider}`,
    minWidth: 220,
    maxWidth: 260,
    overflowY: 'auto',
}));

const CategoryGroup = styled(Box)(({ theme }) => ({
    '&:not(:last-child)': {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
}));

const CategoryLabel = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(1, 2),
    fontWeight: theme.typography.fontWeightSemiBold,
    fontSize: theme.typography.size.sm,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    background: theme.palette.action.hover,
}));

const ModifiedBadge = styled(Chip)(({ theme }) => ({
    marginLeft: theme.spacing(0.5),
    height: 18,
    fontSize: theme.typography.size.xs,
}));

const EditorPanel = styled(Box)(({ theme }) => ({
    flex: 1,
    padding: theme.spacing(2),
    overflowY: 'auto',
}));

const TemplateLayout = styled(Box)(({ theme }) => ({
    display: 'flex',
    minHeight: 400,
    maxHeight: 'calc(100vh - 340px)',
}));

const DynamicFieldsCard = styled(Card)(({ theme }) => ({
    position: 'sticky',
    top: theme.spacing(2),
}));

const DynamicFieldsHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
}));

const FieldCodeContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const FieldChip = styled(Chip)(({ theme }) => ({
    height: theme.spacing(2),
    fontSize: theme.typography.size.xs,
}));

const NotificationTemplates = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [metadata, setMetadata] = useState({});
    const [placeholderFields, setPlaceholderFields] = useState({ _common: [] });
    const [defaults, setDefaults] = useState({ email: {}, inApp: {}, apprise: {} });
    const [overrides, setOverrides] = useState({ email: {}, inApp: {}, apprise: {} });
    const [globalChannel, setGlobalChannel] = useState('email');
    const [selectedObjectType, setSelectedObjectType] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);

    useEffect(() => {
        setLoading(true);
        dispatch(getConfigValue({
            key: 'notificationTemplatesRegistry',
            value: null,
            callback: (data) => {
                const bundle = data?.value;
                if (bundle && typeof bundle === 'object') {
                    setMetadata(bundle.metadata || {});
                    setPlaceholderFields(bundle.placeholderFields || { _common: [] });
                    setDefaults(bundle.defaults || { email: {}, inApp: {}, apprise: {} });
                    setOverrides(bundle.overrides || { email: {}, inApp: {}, apprise: {} });
                }
                setLoading(false);
            },
        }));
    }, [dispatch]);

    const objectTypes = useMemo(() => Object.keys(metadata), [metadata]);

    const eventMeta = selectedObjectType && selectedEvent ? metadata[selectedObjectType]?.[selectedEvent] : null;
    const supportedChannels = eventMeta?.channels || [];

    useEffect(() => {
        if (eventMeta && !supportedChannels.includes(globalChannel) && supportedChannels.length > 0) {
            setGlobalChannel(supportedChannels[0]);
        }
    }, [selectedObjectType, selectedEvent]); // eslint-disable-line react-hooks/exhaustive-deps

    const getDefault = (channel, objectType, event) => defaults[channel]?.[objectType]?.[event] || {};
    const getOverride = (channel, objectType, event) => overrides[channel]?.[objectType]?.[event] || {};

    const getEffective = (channel, objectType, event) => {
        const def = getDefault(channel, objectType, event);
        const ov = getOverride(channel, objectType, event);
        return {
            subject: hasValue(ov.subject) ? ov.subject : def.subject || '',
            body: hasValue(ov.body) ? ov.body : def.body || '',
        };
    };

    const isOverridden = (channel, objectType, event) => {
        const def = getDefault(channel, objectType, event);
        const ov = getOverride(channel, objectType, event);
        return (hasValue(ov.subject) && ov.subject !== def.subject) || (hasValue(ov.body) && ov.body !== def.body);
    };

    const isEventOverriddenAnyChannel = (objectType, event) =>
        (metadata[objectType]?.[event]?.channels || []).some((ch) => isOverridden(ch, objectType, event));

    const handleFieldChange = (channel, objectType, event, field, value) => {
        setOverrides((prev) => ({
            ...prev,
            [channel]: {
                ...prev[channel],
                [objectType]: {
                    ...prev[channel]?.[objectType],
                    [event]: {
                        ...prev[channel]?.[objectType]?.[event],
                        [field]: value,
                    },
                },
            },
        }));
    };

    const handleResetChannel = (channel, objectType, event) => {
        setOverrides((prev) => {
            const channelOverrides = { ...prev[channel] };
            if (channelOverrides[objectType]) {
                const events = { ...channelOverrides[objectType] };
                delete events[event];
                if (Object.keys(events).length === 0) {
                    delete channelOverrides[objectType];
                } else {
                    channelOverrides[objectType] = events;
                }
            }
            return { ...prev, [channel]: channelOverrides };
        });
        dispatch(showAlert({ severity: 'info', message: `Reset ${CHANNEL_LABELS[channel]} content to default` }));
    };

    const handleSave = () => {
        setSaving(true);
        let pending = 3;
        let hadError = false;
        const done = (data) => {
            if (data?.status === 0) hadError = true;
            pending -= 1;
            if (pending <= 0) {
                setSaving(false);
                if (!hadError) {
                    dispatch(showAlert({ severity: 'success', message: 'Notification templates saved successfully' }));
                }
            }
        };
        Object.entries(CHANNEL_KEYS).forEach(([channel, key]) => {
            dispatch(saveConfig({ key, value: overrides[channel] || {}, callback: done }));
        });
    };

    const handleFieldCopy = (field) => {
        navigator.clipboard.writeText(field);
        dispatch(showAlert({ severity: 'info', message: `Copied ${field} to clipboard` }));
    };

    const renderChannelFields = (channel, objectType, event) => {
        const effective = getEffective(channel, objectType, event);

        if (channel === 'email') {
            return (
                <Box>
                    <TextField
                        fullWidth
                        label="Subject"
                        value={effective.subject}
                        onChange={(e) => handleFieldChange('email', objectType, event, 'subject', e.target.value)}
                        size="small"
                    />
                    <Typography variant="caption" color="text.secondary" display="block" mt={2} mb={1}>
                        Body (Rich Text)
                    </Typography>
                    <MarkdownEditor
                        key={`email/${objectType}/${event}`}
                        initialContent={effective.body || null}
                        onChange={(json) => handleFieldChange('email', objectType, event, 'body', json)}
                        autoFocus={false}
                        ignoreSelectionChange
                        placeholder="Email body content..."
                    />
                </Box>
            );
        }

        const isApprise = channel === 'apprise';
        return (
            <Box>
                <TextField
                    fullWidth
                    label={isApprise ? 'Subject' : 'Subject (notification title)'}
                    value={effective.subject}
                    onChange={(e) => handleFieldChange(channel, objectType, event, 'subject', e.target.value)}
                    size="small"
                />
                <TextField
                    fullWidth
                    multiline
                    rows={isApprise ? 4 : 3}
                    label={isApprise ? 'Body' : 'Body (plain text)'}
                    value={effective.body}
                    onChange={(e) => handleFieldChange(channel, objectType, event, 'body', e.target.value)}
                    size="small"
                    helperText={isApprise ? 'Markdown supported. Use dynamic fields like {object.name}.' : 'Plain text only. Use dynamic fields like {object.name}.'}
                    style={{ marginTop: 16 }}
                />
            </Box>
        );
    };

    const placeholderGroups = useMemo(() => {
        const common = placeholderFields._common || [];
        const specific = selectedObjectType && placeholderFields[selectedObjectType] ? placeholderFields[selectedObjectType] : [];
        const groups = { Common: common };
        if (specific.length > 0) groups[`${selectedObjectType} Fields`] = specific;
        return groups;
    }, [placeholderFields, selectedObjectType]);

    return (
        <AdminPageContainer>
            <PageHeader title="Configuration" subtitle="Manage system configuration settings" />

            <ConfigSubMenu />

            {loading ? (
                <LoadingContainer>
                    <CircularProgress />
                </LoadingContainer>
            ) : (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <GlobalChannelSelector>
                            <Typography variant="subtitle2" color="text.secondary">
                                Channel:
                            </Typography>
                            <Tabs
                                value={globalChannel}
                                onChange={(_, value) => setGlobalChannel(value)}
                                variant="standard"
                            >
                                {['email', 'inApp', 'apprise'].map((channel) => (
                                    <Tab
                                        key={channel}
                                        value={channel}
                                        icon={CHANNEL_ICONS[channel]}
                                        iconPosition="start"
                                        label={CHANNEL_LABELS[channel]}
                                        disabled={!!eventMeta && !supportedChannels.includes(channel)}
                                    />
                                ))}
                            </Tabs>
                        </GlobalChannelSelector>

                        <StaticCard style={{ padding: 0, overflow: 'hidden' }}>
                            <TemplateLayout>
                                <NavPanel>
                                    {objectTypes.map((objectType) => {
                                        const events = Object.keys(metadata[objectType] || {});
                                        return (
                                            <CategoryGroup key={objectType}>
                                                <CategoryLabel>{objectType}</CategoryLabel>
                                                <List dense disablePadding>
                                                    {events.map((event) => {
                                                        const meta = metadata[objectType][event];
                                                        const isSelected = selectedObjectType === objectType && selectedEvent === event;
                                                        return (
                                                            <ListItemButton
                                                                key={event}
                                                                selected={isSelected}
                                                                onClick={() => { setSelectedObjectType(objectType); setSelectedEvent(event); }}
                                                            >
                                                                <ListItemText
                                                                    primary={meta.label}
                                                                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                                                />
                                                                {isEventOverriddenAnyChannel(objectType, event) && (
                                                                    <ModifiedBadge label="customized" size="small" color="info" variant="outlined" />
                                                                )}
                                                                <ChevronRightIcon fontSize="small" color="action" />
                                                            </ListItemButton>
                                                        );
                                                    })}
                                                </List>
                                            </CategoryGroup>
                                        );
                                    })}
                                </NavPanel>

                                <EditorPanel>
                                    {eventMeta ? (
                                        <>
                                            <EventHeader>
                                                <EventNameRow>
                                                    <Typography variant="subtitle1" fontWeight="bold">
                                                        {eventMeta.label}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {eventMeta.description}
                                                    </Typography>
                                                </EventNameRow>
                                                {supportedChannels.includes(globalChannel) && isOverridden(globalChannel, selectedObjectType, selectedEvent) && (
                                                    <HoverTooltip title={`Reset ${CHANNEL_LABELS[globalChannel]} content to the built-in default`}>
                                                        <Button
                                                            size="small"
                                                            color="inherit"
                                                            startIcon={<RestartAltIcon fontSize="small" />}
                                                            onClick={() => handleResetChannel(globalChannel, selectedObjectType, selectedEvent)}
                                                        >
                                                            Reset to default
                                                        </Button>
                                                    </HoverTooltip>
                                                )}
                                            </EventHeader>
                                            {supportedChannels.includes(globalChannel) ? (
                                                renderChannelFields(globalChannel, selectedObjectType, selectedEvent)
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    This event does not send on the {CHANNEL_LABELS[globalChannel]} channel.
                                                </Typography>
                                            )}
                                        </>
                                    ) : (
                                        <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                                            <Typography variant="body2" color="text.secondary">
                                                Select an event from the list to edit its content
                                            </Typography>
                                        </Box>
                                    )}
                                </EditorPanel>
                            </TemplateLayout>
                        </StaticCard>

                        <StickyFooter>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<SaveIcon />}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save All Templates'}
                            </Button>
                        </StickyFooter>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <DynamicFieldsCard>
                            <CardContent>
                                <DynamicFieldsHeader>
                                    <InfoIcon color="primary" />
                                    <Typography variant="h6">Dynamic Fields</Typography>
                                </DynamicFieldsHeader>
                                <Typography variant="body2" color="text.secondary" style={{ marginBottom: 16 }}>
                                    Click a field to copy it to the clipboard.
                                </Typography>

                                {Object.entries(placeholderGroups).map(([groupName, fields]) => (
                                    <Box key={groupName} style={{ marginBottom: 16 }}>
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            {groupName}
                                        </Typography>
                                        <List dense>
                                            {fields.map(({ field, description, channels }) => (
                                                <ListItem
                                                    key={field}
                                                    button
                                                    onClick={() => handleFieldCopy(field)}
                                                    style={{ borderRadius: 4, marginBottom: 4 }}
                                                >
                                                    <ListItemText
                                                        primary={
                                                            <FieldCodeContainer>
                                                                <Typography variant="caption" component="code" style={{ fontFamily: 'monospace' }}>
                                                                    {field}
                                                                </Typography>
                                                                {channels !== 'all' && (
                                                                    <FieldChip label={channels} size="small" variant="outlined" />
                                                                )}
                                                            </FieldCodeContainer>
                                                        }
                                                        secondary={description}
                                                        secondaryTypographyProps={{ variant: 'caption' }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                        <Divider style={{ marginTop: 8 }} />
                                    </Box>
                                ))}
                            </CardContent>
                        </DynamicFieldsCard>
                    </Grid>
                </Grid>
            )}
        </AdminPageContainer>
    );
};

export default NotificationTemplates;
